import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { ChartFormatModel }    from '../typings/chart-format';
import { ChartFormatService }  from '../chart-format.service';
import { ChartInfoPanelModel } from '../typings/chart-info-panel';
import { ChartPointModel }     from '../typings/chart-point';
import { ChartSeriesModel }    from '../typings/chart-series';
import { infoPanelPosition }   from '../chart-info-panel/chart-info-panel-position';

interface RenderedRow {
  bars : Array<{ color : string; percent : number; }>;
  label: string;
  point: ChartPointModel;
  value: string;
}

// Horizontal bars, one labelled row per point scaled to the largest; hover reads out, click drills
@Component({
  selector   : 'wor-hbar-chart',
  styles: [ require('./hbar-chart.component.scss') ],
  template: require('./hbar-chart.component.html')
})
export class HbarChartComponent implements OnChanges {
  @Input() format : ChartFormatModel        = 'money';
  @Input() points : Array<ChartPointModel>  = [];
  @Input() series : Array<ChartSeriesModel> = [];

  @Output() rowPressed : EventEmitter<ChartPointModel> = new EventEmitter<ChartPointModel>();

  public hiddenKeys : Array<string>       = [];
  public infoPanel  : ChartInfoPanelModel = { isVisible: false, rows: [], title: '', x: 0, y: 0 };
  public rows       : Array<RenderedRow>  = [];

  constructor (
    private chartFormatService: ChartFormatService
  ) {}

  ngOnChanges () : void {
    // a series that is no longer supplied cannot stay hidden
    this.hiddenKeys = this.hiddenKeys.filter(key => this.series.some(item => item.key === key));

    this.render();
  }

  formatValue ( value : number ) : string {
    return this.chartFormatService.value(value, this.format);
  }

  hideInfoPanel () : void {
    this.infoPanel = { ...this.infoPanel, isVisible: false };
  }

  isDrillable () : boolean {
    return this.rowPressed.observed;
  }

  isHidden ( key : string ) : boolean {
    return this.hiddenKeys.indexOf(key) > -1;
  }

  onMove ( event : MouseEvent, row : RenderedRow ) : void {
    const wrapper = ( event.currentTarget as HTMLElement ).closest('.chart-wrapper') as HTMLElement;
    const bounds = wrapper.getBoundingClientRect();

    this.infoPanel = {
      isVisible: true,
      rows     : this.visibleSeries().map(item => ({
        color: item.color,
        label: item.label,
        value: this.formatValue(row.point.values[ item.key ] || 0)
      })),
      title: row.label,
      ...infoPanelPosition(event, bounds)
    };
  }

  press ( row : RenderedRow ) : void {
    if ( this.isDrillable() ) {
      this.rowPressed.emit(row.point);
    }
  }

  toggleSeries ( key : string ) : void {
    if ( !this.isHidden(key) && this.visibleSeries().length < 2 ) {
      return;
    }

    this.hiddenKeys = this.isHidden(key)
      ? this.hiddenKeys.filter(item => item !== key)
      : this.hiddenKeys.concat(key);

    this.render();
  }

  trackByIndex ( index : number ) : number {
    return index;
  }

  visibleSeries () : Array<ChartSeriesModel> {
    return this.series.filter(item => !this.isHidden(item.key));
  }

  private render () : void {
    this.hideInfoPanel();
    this.rows = [];

    if ( !this.points.length || !this.visibleSeries().length ) {
      return;
    }

    let max = 0;

    this.points.forEach(point => {
      this.visibleSeries().forEach(item => max = Math.max(max, point.values[ item.key ] || 0));
    });

    if ( max <= 0 ) {
      max = 1;
    }

    this.rows = this.points.map(point => ({
      bars: this.visibleSeries().map(item => ({
        color  : item.color,
        percent: Math.max(( ( point.values[ item.key ] || 0 ) / max ) * 100, 0)
      })),
      label: point.label,
      point,
      value: this.formatValue(point.values[ this.visibleSeries()[ 0 ].key ] || 0)
    }));
  }
}
